import React, { createContext, useContext, useEffect, useState } from "react";
import MultiSelectDropdown from './components/ui/MultiSelectDropdown';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./styles/globals.css";
import Privacy from "./screens/Privacy";
import Login from "./screens/Login";
import Dashboard from "./screens/Dashboard";
import DashboardContent from "./screens/DashboardContent";
import MyScribes from "./screens/MyScribes";
import Calls from "./screens/Calls";
import AIStudio from "./screens/AIStudio";
import Images from "./screens/Images";
import AudioRecorder from "./screens/Record";
import DoctorOnboarding from "./screens/DoctorOnboarding";
import Settings from "./screens/Settings";
import NotFound from "./screens/NotFound";
import Patients from "./screens/Patients";
import PricingPlans from "./components/ui/PricingPlans";
import PaymentSuccessPage from "./screens/PaymentSuccessPage";
import PaymentCancelPage from "./screens/PaymentCancelPage";
import { authService } from "./services/authService";
import { websocketService } from "./services/websocketService";
import TrialBanner from "./components/ui/TrialBanner";
import { useSubscription } from "./hooks/useSubscription";
import { useUserProfile } from './hooks/useUserProfile';
import PaymentStatus from "./components/ui/PaymentStatus";
import HubSpotChat from "./components/ui/HubSpotChat";
import FAQ from "./screens/FAQ";
import ProfileRestrictedRoute from "./components/ProfileRestrictedRoute";
import DeveloperPortal from "./screens/DeveloperPortal";
import { developerApiService, ApiLog } from "./services/developerApiService";
import AnalyticsDashboard from './screens/AnalyticsDashboard';

import DeveloperMetrics from './components/DeveloperMetrics';
import Docs from './screens/Docs';
import Prompts from './screens/Prompts';
import SearchTemplates from './screens/SearchTemplates';

// Create Authentication Context
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  checkAuth: async () => {},
});

// Auth Provider Component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const checkAuth = async () => {
    console.log("AuthContext.checkAuth - Checking authentication status");
    setIsLoading(true);
    const authenticated = authService.isAuthenticated();
    console.log("AuthContext.checkAuth - Authentication result:", authenticated);
    setIsAuthenticated(authenticated);
    
    // Initialize WebSocket connection if user is authenticated (non-blocking)
    if (authenticated) {
      console.log("🔌 User authenticated - initializing WebSocket connection (non-blocking)");
      // Don't await WebSocket connection - let it connect in background
      websocketService.connect().then(connected => {
        if (connected) {
          console.log("✅ WebSocket connection established on auth check");
        } else {
          console.log("❌ WebSocket connection failed on auth check");
        }
      }).catch(error => {
        console.error("❌ Error initializing WebSocket on auth check:", error);
      });
    } else {
      // Disconnect WebSocket if user is not authenticated
      console.log("🔌 User not authenticated - disconnecting WebSocket");
      websocketService.disconnect();
    }
    
    setIsLoading(false);
    console.log("AuthContext.checkAuth - Updated context state, isLoading:", false, "isAuthenticated:", authenticated);
  };

  useEffect(() => {
    checkAuth();
    
    // Cleanup WebSocket connection on unmount
    return () => {
      console.log("🧹 AuthContext unmounting - cleaning up WebSocket");
      websocketService.disconnect();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Create an authenticated layout component with sidebar
const AuthenticatedLayout = ({ children }: { children: React.ReactNode }) => {
  const { isTrial, isFree, trialEnd, isLoading, isCanceled, shouldShowBanner, status } = useSubscription();
  const { isDeveloper } = useUserProfile();
  const [isDeveloperPanelOpen, setIsDeveloperPanelOpen] = React.useState(false);

  // Panel tabs
  const [activePanelTab, setActivePanelTab] = React.useState('logs');
  const [isFullExpanded, setIsFullExpanded] = React.useState(false);

  // Logs state
  const [apiLogs, setApiLogs] = React.useState<ApiLog[]>([]);
  const [selectedLog, setSelectedLog] = React.useState<ApiLog | null>(null);
  const [isLogsLoading, setIsLogsLoading] = React.useState(false);
  const [isLogDetailLoading, setIsLogDetailLoading] = React.useState(false);
  const [loadingLogId, setLoadingLogId] = React.useState<string | null>(null);

  // Request search state
  const [requestId, setRequestId] = React.useState('');
  const [requestData, setRequestData] = React.useState<ApiLog | null>(null);
  const [isSearchingRequest, setIsSearchingRequest] = React.useState(false);
  const [requestSearchError, setRequestSearchError] = React.useState<string | null>(null);

  // Filter state
  const [methodFilter, setMethodFilter] = React.useState<string[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [endpointFilter, setEndpointFilter] = React.useState<string>('');

  // Parse trialEnd string to Date if it exists
  const trialEndDate = trialEnd ? new Date(trialEnd) : null;

  // Check if we should show the banner considering all possible states
  const showBanner = !isLoading && (shouldShowBanner || status === 'free');


  // Fetch logs function (accept optional API key to forward in header)
  const fetchApiLogs = async (apiKey?: string) => {
    try {
      setIsLogsLoading(true);
      const logs = await developerApiService.getApiLogs({ limit: 50 }, apiKey);
      setApiLogs(logs);
    } catch (err: any) {
      console.error('Failed to fetch API logs:', err);
    } finally {
      setIsLogsLoading(false);
    }
  };

  // Fetch log detail function (forwards optional API key)
  const fetchLogDetail = async (logId: string, apiKey?: string) => {
    // Set loading states immediately
    setLoadingLogId(logId);
    setIsLogDetailLoading(true);
    setSelectedLog(null); // Clear previous selection

    try {
      // Add minimum loading time to ensure loader is visible
      const [logDetail] = await Promise.all([
        developerApiService.getApiLog(logId, apiKey),
        new Promise(resolve => setTimeout(resolve, 500)) // Minimum 500ms loading time
      ]);

      // If server didn't include which api key was used, set it from the key we used
      if (!logDetail.api_key_used && apiKey) {
        logDetail.api_key_used = apiKey;
      }
      setSelectedLog(logDetail);
    } catch (err: any) {
      console.error('Failed to fetch log details:', err);
    } finally {
      setIsLogDetailLoading(false);
      setLoadingLogId(null);
    }
  };

  // Search request by ID function
  const searchRequestById = async () => {
    if (!requestId.trim()) {
      setRequestSearchError('Please enter a request ID');
      return;
    }

    try {
      setIsSearchingRequest(true);
      setRequestSearchError(null);
      const logDetail = await developerApiService.getApiLog(requestId.trim());
      setRequestData(logDetail);
    } catch (err: any) {
      console.error('Failed to fetch request details:', err);
      setRequestSearchError(err.message || 'Failed to fetch request details');
      setRequestData(null);
    } finally {
      setIsSearchingRequest(false);
    }
  };


  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'text-blue-600 bg-blue-50';
      case 'POST': return 'text-green-600 bg-green-50';
      case 'PUT': return 'text-yellow-600 bg-yellow-50';
      case 'DELETE': return 'text-red-600 bg-red-50';
      case 'PATCH': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatLogDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Fetch logs when panel opens
  React.useEffect(() => {
    if (isDeveloperPanelOpen) {
      fetchApiLogs();
    }
  }, [isDeveloperPanelOpen]);

  // Filter logs based on current filters
  const filteredLogs = React.useMemo(() => {
    return apiLogs.filter(log => {
      // Method match: accept if no method selected OR the log method is included in selected methods
      const methodMatches = methodFilter.length === 0 || methodFilter.includes(log.method);

      // Status match: accept if no status selected OR any selected status range matches the log.status
      const statusMatches = statusFilter.length === 0 || statusFilter.some(statusRange => {
        if (!statusRange) return true; // the "All statuses" empty option
        if (statusRange.endsWith('xx')) {
          const prefix = parseInt(statusRange[0], 10);
          return log.status >= prefix * 100 && log.status < (prefix + 1) * 100;
        }
        return false;
      });

      const endpointMatches = !endpointFilter || log.endpoint.toLowerCase().includes(endpointFilter.toLowerCase());

      return methodMatches && statusMatches && endpointMatches;
    });
  }, [apiLogs, methodFilter, statusFilter, endpointFilter]);

  // Group filtered logs by date
  const groupedLogs = React.useMemo(() => {
    const groups: { [key: string]: typeof filteredLogs } = {};
    filteredLogs.forEach(log => {
      const date = new Date(log.created).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric'
      });
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(log);
    });
    return groups;
  }, [filteredLogs]);

  // Add debug logging
  console.log('AuthenticatedLayout - Banner conditions:', {
    isLoading,
    shouldShowBanner,
    showBanner,
    status,
    isFree,
    isTrial,
    isCanceled
  });

  const DeveloperConsole = <>
    <div
      className="bg-black text-white flex-shrink-0 z-50 cursor-pointer hover:bg-gray-900 transition-colors"
      onClick={() => setIsDeveloperPanelOpen(!isDeveloperPanelOpen)}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm">Developers</span>
            </div>
           
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-300">
            <span className="text-gray-500">v1.0</span>
            <svg
              className={`w-4 h-4 transition-transform ${isDeveloperPanelOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    {/* Developer Panel - Slide up from bottom */}
    <div className={`fixed ${isFullExpanded ? 'inset-0' : 'inset-x-0'} bg-black border-t border-gray-800 z-40 transform transition-all duration-300 ease-in-out ${isDeveloperPanelOpen ? 'translate-y-0' : 'translate-y-full'}`} style={{
        height: isFullExpanded
          ? `calc(100vh${showBanner ? ' - 40px' : ''})`
          : '90vh',
        top: isFullExpanded && showBanner ? '40px' : isFullExpanded ? '0' : 'auto',
        bottom: isFullExpanded ? 'auto' : '0'
      }}>
      <div className="h-full flex flex-col">
        {/* Panel Header with Tabs */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-black">
          <div className="flex items-center space-x-8">
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Developer Tools</h3>
            <div className="flex items-center space-x-0">
              <button
                onClick={() => setActivePanelTab('logs')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activePanelTab === 'logs'
                    ? 'border-blue-400 text-blue-400 bg-gray-900'
                    : 'border-transparent text-gray-400 hover:text-white'}`}
                style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
              >
                Logs
              </button>
              <button
                onClick={() => setActivePanelTab('metrics')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activePanelTab === 'metrics'
                    ? 'border-blue-400 text-blue-400 bg-gray-900'
                    : 'border-transparent text-gray-400 hover:text-white'}`}
                style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
              >
                Metrics
              </button>
              <button
                onClick={() => setActivePanelTab('request-search')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activePanelTab === 'request-search'
                    ? 'border-blue-400 text-blue-400 bg-gray-900'
                    : 'border-transparent text-gray-400 hover:text-white'}`}
                style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
              >
                Request Search
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsFullExpanded(!isFullExpanded)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors"
              title={isFullExpanded ? "Minimize" : "Expand"}
            >
              {isFullExpanded ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7-7m0 0l-7 7m7-7v18" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h10v10" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7" />
                </svg>
              )}
            </button>
            <button
              onClick={() => setIsDeveloperPanelOpen(false)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Panel Content */}
        <div className="flex-1 overflow-hidden bg-black">
          {activePanelTab === 'logs' && (
            <div className="flex h-full min-h-0">
              {/* Logs List - Black Theme */}
              <div className="w-1/2 border-r border-gray-800 bg-black flex flex-col min-h-0">
                <div className="px-4 py-3 border-b border-gray-800 bg-black">
                  {/* Stripe-style Filters */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="relative">
                        <MultiSelectDropdown
                          options={[
                            { value: 'GET', label: 'GET' },
                            { value: 'POST', label: 'POST' },
                            { value: 'PUT', label: 'PUT' },
                            { value: 'DELETE', label: 'DELETE' },
                            { value: 'PATCH', label: 'PATCH' },
                          ]}
                          selected={methodFilter}
                          onChange={(vals) => setMethodFilter(vals)}
                          placeholder="All methods"
                        />
                      </div>

                      <div className="relative">
                        <MultiSelectDropdown
                          options={[
                            { value: '1xx', label: '1xx Informational' },
                            { value: '2xx', label: '2xx Success' },
                            { value: '3xx', label: '3xx Redirect' },
                            { value: '4xx', label: '4xx Client Error' },
                            { value: '5xx', label: '5xx Server Error' },
                          ]}
                          selected={statusFilter}
                          onChange={(vals) => setStatusFilter(vals)}
                          placeholder="All statuses"
                        />
                      </div>

                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Filter by endpoint"
                          value={endpointFilter}
                          onChange={(e) => setEndpointFilter(e.target.value)}
                          className="bg-gray-900 border border-gray-700 rounded-md px-2.5 py-1.5 text-xs text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-40"
                          style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }} />
                      </div>
                    </div>

                    <button
                      onClick={() => fetchApiLogs()}
                      disabled={isLogsLoading}
                      className="inline-flex items-center p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded transition-colors"
                    >
                      <svg className={`h-3.5 w-3.5 ${isLogsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-auto pb-4">
                  {isLogsLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="flex items-center space-x-2 text-gray-400">
                        <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading requests...</span>
                      </div>
                    </div>
                  ) : apiLogs.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-gray-400">
                      <div className="text-center">
                        <svg className="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm">No requests yet</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs bg-black">
                      <div className="px-4 py-1.5 border-b border-gray-800 text-gray-500">
                        <div className="grid grid-cols-12 gap-1 text-xs" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                          <div className="col-span-2 font-medium">Status</div>
                          <div className="col-span-2 font-medium">Method</div>
                          <div className="col-span-6 font-medium">Path</div>
                          <div className="col-span-2 font-medium">Time</div>
                        </div>
                      </div>
                      {Object.entries(groupedLogs)
                        .sort(([a], [b]) => new Date(b + ', 2024').getTime() - new Date(a + ', 2024').getTime())
                        .map(([date, logs]) => (
                          <div key={date}>
                            {/* Date Header */}
                            <div className="px-4 py-2 bg-gray-900 border-b border-gray-800 text-gray-300 font-medium text-xs" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                              {date}
                            </div>
                            {/* Logs for this date */}
                            {logs.map((log) => (
                              <div
                                key={log.id}
                                className={`px-4 py-1.5 cursor-pointer transition-colors hover:bg-gray-900 border-l-2 ${
                                  selectedLog?.id === log.id
                                    ? 'bg-gray-800 border-blue-400'
                                    : loadingLogId === log.id
                                    ? 'bg-gray-800 border-yellow-400'
                                    : 'border-transparent'
                                }`}
                                onClick={() => {
                                  const keyToUse = log.api_key_used || undefined;
                                  fetchLogDetail(log.id, keyToUse);
                                }}
                              >
                                <div className="grid grid-cols-12 gap-1 items-center text-xs" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                                  <div className="col-span-2">
                                    <span className={`font-medium whitespace-nowrap ${log.status >= 200 && log.status < 300 ? 'text-green-400' :
                                        log.status >= 400 ? 'text-red-400' : 'text-gray-300'}`}>
                                      {log.status} {log.status >= 200 && log.status < 300 ? 'OK' :
                                        log.status >= 400 ? 'Error' : 'Unknown'}
                                    </span>
                                  </div>
                                  <div className="col-span-2 text-blue-400 font-medium whitespace-nowrap">
                                    {log.method}
                                  </div>
                                  <div className="col-span-6 text-white truncate">
                                    {log.endpoint}
                                  </div>
                                  <div className="col-span-2 text-gray-400 whitespace-nowrap text-right flex items-center justify-end">
                                    {loadingLogId === log.id ? (
                                      <svg className="h-3 w-3 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    ) : null}
                                    <span>
                                      {new Date(log.created).toLocaleTimeString('en-US', {
                                        hour12: true,
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        second: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Request Details Panel - Black Theme */}
              <div className="flex-1 bg-black flex flex-col min-h-0">
                {selectedLog ? (
                  <>
                        <div className="bg-black border-b border-gray-800 p-6 flex-shrink-0">
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium ${getMethodColor(selectedLog.method)}`}>
                          {selectedLog.method}
                        </span>
                        <code className="text-sm text-white font-mono bg-gray-800 px-2 py-1 rounded" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>{selectedLog.endpoint}</code>
                        <span className={`text-sm font-semibold ${selectedLog.status >= 200 && selectedLog.status < 300 ? 'text-green-400' :
                            selectedLog.status >= 400 ? 'text-red-400' : 'text-gray-300'}`}>
                          {selectedLog.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-xs text-gray-400" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>{formatLogDate(selectedLog.created)}</div>
                        <div className="text-xs text-gray-400 font-mono">Key used: <span className="text-gray-200 ml-1">{selectedLog.api_key_used || '—'}</span></div>
                      </div>
                    </div>

                    <div className="flex-1 overflow-auto p-6 pb-8 space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-400 mb-1" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Response Time</div>
                          <div className="text-sm text-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>{selectedLog.response_time_ms.toFixed(2)}ms</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400 mb-1" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>IP Address</div>
                          <div className="text-sm text-white font-mono" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>{selectedLog.ip_address}</div>
                        </div>
                      </div>

                      {selectedLog.request_headers && (
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Headers</h4>
                          <div className="bg-gray-900 rounded-md p-3 text-xs text-gray-300 overflow-auto border border-gray-800">
                            <pre className="whitespace-pre-wrap break-words" style={{ fontFamily: 'Monaco, Consolas, monospace' }}>{JSON.stringify(selectedLog.request_headers, null, 2)}</pre>
                          </div>
                        </div>
                      )}

                      {selectedLog.request_body && (
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Request</h4>
                          <div className="bg-gray-900 rounded-md p-3 text-xs text-gray-300 overflow-auto border border-gray-800">
                            <pre className="whitespace-pre-wrap break-words" style={{ fontFamily: 'Monaco, Consolas, monospace' }}>{JSON.stringify(selectedLog.request_body, null, 2)}</pre>
                          </div>
                        </div>
                      )}

                      {selectedLog.response_body && (
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Response</h4>
                          <div className="bg-gray-900 rounded-md p-3 text-xs text-gray-300 overflow-auto border border-gray-800">
                            <pre className="whitespace-pre-wrap break-words" style={{ fontFamily: 'Monaco, Consolas, monospace' }}>{JSON.stringify(selectedLog.response_body, null, 2)}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : isLogDetailLoading ? (
                  <div className="h-full flex items-center justify-center text-gray-400 bg-gray-900">
                    <div className="flex flex-col items-center space-y-4 p-8">
                      <svg className="h-12 w-12 animate-spin text-blue-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <div className="text-center">
                        <p className="text-lg font-medium text-white mb-1" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Loading Request Details</p>
                        <p className="text-sm text-gray-400" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Please wait while we fetch the data...</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <svg className="h-16 w-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-lg font-medium text-gray-300 mb-1" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Select a request</p>
                      <p className="text-sm text-gray-400" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Choose a request to view details</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activePanelTab === 'metrics' && (
            <DeveloperMetrics />
          )}

          {activePanelTab === 'request-search' && (
            <div className="h-full bg-black">
              <div className="p-6">
                <div className="max-w-2xl">
                  <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                    Search Request by ID
                  </h3>
                  <p className="text-sm text-gray-400 mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                    Enter a request ID to retrieve detailed information about a specific API request.
                  </p>

                  <div className="space-y-4">
                    <div className="flex space-x-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Enter request ID (e.g., req_qo8hl36bK3zdZDBNE4Plg4d4)"
                          value={requestId}
                          onChange={(e) => setRequestId(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && searchRequestById()}
                          className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                        />
                      </div>
                      <button
                        onClick={searchRequestById}
                        disabled={isSearchingRequest || !requestId.trim()}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                      >
                        {isSearchingRequest ? (
                          <div className="flex items-center space-x-2">
                            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Searching...</span>
                          </div>
                        ) : (
                          'Search'
                        )}
                      </button>
                    </div>

                    {requestSearchError && (
                      <div className="bg-red-900/20 border border-red-500/30 rounded-md p-3">
                        <div className="flex items-start space-x-2">
                          <svg className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-red-300" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                            {requestSearchError}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {requestData ? (
                <div className="border-t border-gray-800 h-full overflow-auto">
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-6">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium ${getMethodColor(requestData.method)}`}>
                        {requestData.method}
                      </span>
                      <code className="text-sm text-white font-mono bg-gray-800 px-2 py-1 rounded" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                        {requestData.endpoint}
                      </code>
                      <span className={`text-sm font-semibold ${
                        requestData.status >= 200 && requestData.status < 300 ? 'text-green-400' :
                        requestData.status >= 400 ? 'text-red-400' : 'text-gray-300'
                      }`}>
                        {requestData.status}
                      </span>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-400 mb-1" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Request ID</div>
                          <div className="text-sm text-white font-mono" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>{requestData.id}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400 mb-1" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Response Time</div>
                          <div className="text-sm text-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>{requestData.response_time_ms.toFixed(2)}ms</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400 mb-1" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Timestamp</div>
                          <div className="text-sm text-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>{formatLogDate(requestData.created)}</div>
                        </div>
                      </div>

                      {(requestData as any).usage && (
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Usage & Pricing</h4>
                          <div className="bg-gray-900 rounded-md p-3 text-xs text-gray-300">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="text-gray-400 mb-1">Tokens</div>
                                <div>Prompt: {(requestData as any).usage.prompt_tokens}</div>
                                <div>Completion: {(requestData as any).usage.completion_tokens}</div>
                                <div>Total: {(requestData as any).usage.total_tokens}</div>
                              </div>
                              {(requestData as any).pricing && (
                                <div>
                                  <div className="text-gray-400 mb-1">Cost (USD)</div>
                                  <div>Prompt: ${(requestData as any).pricing.prompt_cost_usd.toFixed(6)}</div>
                                  <div>Completion: ${(requestData as any).pricing.completion_cost_usd.toFixed(6)}</div>
                                  <div>Total: ${(requestData as any).pricing.total_cost_usd.toFixed(6)}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {requestData.request_headers && (
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Request Headers</h4>
                          <div className="bg-gray-900 rounded-md p-3 text-xs text-gray-300 overflow-auto border border-gray-800 max-h-48">
                            <pre className="whitespace-pre-wrap break-words" style={{ fontFamily: 'Monaco, Consolas, monospace' }}>{JSON.stringify(requestData.request_headers, null, 2)}</pre>
                          </div>
                        </div>
                      )}

                      {requestData.request_body && (
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Request Body</h4>
                          <div className="bg-gray-900 rounded-md p-3 text-xs text-gray-300 overflow-auto border border-gray-800 max-h-48">
                            <pre className="whitespace-pre-wrap break-words" style={{ fontFamily: 'Monaco, Consolas, monospace' }}>{JSON.stringify(requestData.request_body, null, 2)}</pre>
                          </div>
                        </div>
                      )}

                      {requestData.response_body && (
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-2" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Response Body</h4>
                          <div className="bg-gray-900 rounded-md p-3 text-xs text-gray-300 overflow-auto border border-gray-800 max-h-64">
                            <pre className="whitespace-pre-wrap break-words" style={{ fontFamily: 'Monaco, Consolas, monospace' }}>{JSON.stringify(requestData.response_body, null, 2)}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-t border-gray-800 flex items-center justify-center h-64">
                  <div className="text-center text-gray-400">
                    <svg className="h-16 w-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-lg font-medium text-gray-300 mb-1">Request Details</p>
                    <p className="text-sm">Enter a request ID above to view detailed information</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* API docs moved to sidebar /docs - removed from developer panel */}

          {activePanelTab === 'console' && (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <svg className="h-16 w-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-lg font-medium text-gray-400 mb-1">Developer Console</p>
                <p className="text-sm">Debug console coming soon</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Overlay */}
    {isDeveloperPanelOpen && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-30"
        onClick={() => setIsDeveloperPanelOpen(false)} />
    )}
  </>;
  return (
    <div className="flex flex-col h-screen max-h-screen overflow-hidden">
      {/* Payment status notification */}
      <PaymentStatus />

      {/* Show banner if user is on free plan, trial, or has a canceled subscription */}
      {showBanner && (
        <div className="z-50 w-full flex-shrink-0">
          <TrialBanner trialEndDate={trialEndDate} />
        </div>
      )}

      {/* Main content - takes remaining height */}
      <div className="flex flex-1 overflow-hidden">
        <Dashboard />

        {/* Content area - contained to prevent overflow */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>

      {/* Developer Status Bar - Full Width (visible for any developer user) */}
      {isDeveloper && (
        DeveloperConsole
      )}

      {/* Add HubSpot Chat (hide for developer users) */}
      {!isDeveloper && (
        <HubSpotChat />
      )}
    </div>
  );
};

// Protected route that also checks if onboarding is complete
const OnboardingCompletedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { needsOnboarding, isChecking } = useOnboardingStatus();

  // Show a loading indicator while determining status
  const allLoading = isLoading || isChecking;

  useEffect(() => {
    // Only process redirects when all loading is complete
    if (!allLoading) {
      console.log("OnboardingCompletedRoute - Auth state:", { 
        isAuthenticated, 
        needsOnboarding,
        path: location.pathname 
      });
      
      if (!isAuthenticated) {
        console.log("OnboardingCompletedRoute - Not authenticated, redirecting to login");
        navigate('/login', { replace: true, state: { from: location } });
      } else if (needsOnboarding) {
        console.log("OnboardingCompletedRoute - Onboarding not complete, redirecting to onboarding");
        navigate('/doctor-onboarding', { replace: true });
      }
    }
  }, [isAuthenticated, needsOnboarding, allLoading, navigate, location]);

  // Show nothing during loading to prevent flash of content
  if (allLoading) {
    console.log("OnboardingCompletedRoute - Still loading, showing nothing");
    return null;
  }

  // Show children only if authenticated and onboarding is complete
  console.log("OnboardingCompletedRoute - Rendering children, authenticated and onboarded:", isAuthenticated && !needsOnboarding);
  return isAuthenticated && !needsOnboarding ? <>{children}</> : null;
};

// Global state for visibility of loading overlay
export const LoadingContext = createContext({
  isPageLoading: false,
  setPageLoading: (loading: boolean) => {}
});

// Custom hook to check if onboarding is needed with improved performance
const useOnboardingStatus = () => {
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  
  useEffect(() => {
    // Function to check onboarding status
    const checkOnboardingStatus = () => {
      setIsChecking(true); // Start checking
      const onboardingStatusValue = localStorage.getItem("onboarding_complete");
      console.log("useOnboardingStatus - Raw localStorage value:", onboardingStatusValue);
      
      // Only consider true if the value is exactly the string "true"
      const onboardingComplete = onboardingStatusValue === "true";
      console.log("useOnboardingStatus - Parsed onboarding complete:", onboardingComplete);
      
      setNeedsOnboarding(!onboardingComplete);
      console.log("useOnboardingStatus - Needs onboarding:", !onboardingComplete);
      setIsChecking(false); // Finished checking
    };
    
    // Initial check
    checkOnboardingStatus();
    
    // Set up event listener for storage changes
    window.addEventListener('storage', checkOnboardingStatus);
    
    // Custom event for local updates
    const handleCustomStorageEvent = () => {
      console.log("useOnboardingStatus - localStorageUpdated event received");
      checkOnboardingStatus();
    };
    window.addEventListener('localStorageUpdated', handleCustomStorageEvent);
    
    return () => {
      window.removeEventListener('storage', checkOnboardingStatus);
      window.removeEventListener('localStorageUpdated', handleCustomStorageEvent);
    };
  }, []);
  
  return { needsOnboarding, isChecking };
};

// Redirect to appropriate route based on auth and onboarding status
const RedirectHome = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { needsOnboarding, isChecking } = useOnboardingStatus();
  
  // Show nothing during initial loading
  if (isLoading || isChecking) {
    console.log("RedirectHome - Still loading, showing nothing");
    return null;
  }
  
  if (isAuthenticated) {
    if (needsOnboarding) {
      console.log("RedirectHome - User authenticated but needs onboarding, redirecting to /doctor-onboarding");
      return <Navigate to="/doctor-onboarding" replace />;
    } else {
      console.log("RedirectHome - User authenticated and onboarded, redirecting to /mynotes");
      return <Navigate to="/mynotes" replace />;
    }
  }
  
  console.log("RedirectHome - User not authenticated, showing login page");
  return (
    <>
      <Login />
    </>
  );
};

// Redirect to appropriate route based on auth and onboarding status
const RedirectLogin = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { needsOnboarding, isChecking } = useOnboardingStatus();
  
  // Show nothing during initial loading
  if (isLoading || isChecking) {
    console.log("RedirectLogin - Still loading, showing nothing");
    return null;
  }
  
  if (isAuthenticated) {
    if (needsOnboarding) {
      console.log("RedirectLogin - User authenticated but needs onboarding, redirecting to /doctor-onboarding");
      return <Navigate to="/doctor-onboarding" replace />;
    } else {
      console.log("RedirectLogin - User authenticated and onboarded, redirecting to /mynotes");
      return <Navigate to="/mynotes" replace />;
    }
  }
  
  console.log("RedirectLogin - User not authenticated, showing login page");
  return (
    <div className="h-screen">
      <Login />
    </div>
  );
};

// Show doctor onboarding for authenticated users, redirect otherwise
const RedirectOnboarding = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { needsOnboarding, isChecking } = useOnboardingStatus();
  
  // Show loading while we determine status
  if (isLoading || isChecking) {
    console.log("RedirectOnboarding - Still loading state");
    return null; // Show nothing while checking auth status
  }
  
  if (!isAuthenticated) {
    console.log("RedirectOnboarding - User not authenticated, redirecting to /login");
    return <Navigate to="/login" replace />;
  }
  
  // If onboarding is already complete, redirect to mynotes
  if (!needsOnboarding) {
    console.log("RedirectOnboarding - Onboarding already complete, redirecting to /mynotes");
    return <Navigate to="/mynotes" replace />;
  }
  
  console.log("RedirectOnboarding - User authenticated, showing onboarding");
  return (
    <div className="h-screen">
      <DoctorOnboarding />
    </div>
  );
};

// (ProtectedRoute removed — authentication is handled inline in route wrappers)

// Routes configuration
const App = () => {
  const [isPageLoading, setPageLoading] = useState(false);
  
  return (
    <div className="App">
      <Router>
        <AuthProvider>
          <LoadingContext.Provider value={{ isPageLoading, setPageLoading }}>
            <Routes>
              {/* Public routes with header and footer */}
              <Route path="/" element={<RedirectHome />} />
              <Route path="/login" element={<RedirectLogin />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/doctor-onboarding" element={<RedirectOnboarding />} />
              <Route path="/pricing" element={<PricingPlans />} />
              <Route path="/faq" element={<FAQ />} />
              
              {/* Payment related routes */}
              <Route path="/billing/success" element={<PaymentSuccessPage />} />
              <Route path="/billing/cancel" element={<PaymentCancelPage />} />

              {/* Authenticated routes with sidebar */}
              <Route
                path="/dashboard"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <DashboardContent />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/mynotes"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <ProfileRestrictedRoute restrictedForProfileTypes={['developer']}>
                        <MyScribes />
                      </ProfileRestrictedRoute>
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/calls"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <Calls />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/ai-studio"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <AIStudio />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/images"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <Images />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/visit"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <ProfileRestrictedRoute restrictedForProfileTypes={['developer']}>
                        <AudioRecorder />
                      </ProfileRestrictedRoute>
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/developer"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <DeveloperPortal onBackToSettings={() => {}} />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <AnalyticsDashboard />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/docs"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <Docs />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/docs/:docId"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <Docs />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/prompts"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <Prompts />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/search-templates"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <SearchTemplates />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <Settings />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route
                path="/patients"
                element={
                  <OnboardingCompletedRoute>
                    <AuthenticatedLayout>
                      <Patients />
                    </AuthenticatedLayout>
                  </OnboardingCompletedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </LoadingContext.Provider>
        </AuthProvider>
      </Router>
    </div>
  );
};

export default App;
